// frontend/src/pages/AboutPage/AboutPage.jsx

import React, { useCallback, useState, useRef  } from 'react';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import './AboutPage.css';

// Animation variants for sections
const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    },
  },
};

const AboutPage = () => {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    // Particle background logic
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } } },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 800 }, value: 80 },
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`ap-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-about" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('aboutPage.helmet.title')}</title>
                <meta name="description" content={t('aboutPage.helmet.description')} />
            </Helmet>
            
            <div className="ap-nav-container">
                <UniversalNav />
            </div>

            <main className="ap-content-wrapper">
                <Box className="ap-glass-container">
                    <Box className="ap-header">
                        <Typography variant="h3" component="h1">
                            {t('aboutPage.mainTitle')}
                        </Typography>
                    </Box>

                    <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                        <Box className="ap-section">
                            <Typography variant="h5" component="h2">{t('aboutPage.mission.title')}</Typography>
                            <Typography paragraph className="mission-quote">
                                {t('aboutPage.mission.quote')}
                            </Typography>
                            <Typography paragraph>
                                {t('aboutPage.mission.paragraph')}
                            </Typography>
                        </Box>
                    </motion.div>

                    <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                        <Box className="ap-section">
                            <Typography variant="h5" component="h2">{t('aboutPage.solution.title')}</Typography>
                            <Typography paragraph>
                                {t('aboutPage.solution.paragraph')}
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText 
                                        primary={t('aboutPage.solution.list.aiAnalysis.primary')} 
                                        secondary={t('aboutPage.solution.list.aiAnalysis.secondary')} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText 
                                        primary={t('aboutPage.solution.list.fingerprinting.primary')} 
                                        secondary={t('aboutPage.solution.list.fingerprinting.secondary')} />
                                </ListItem>
                                <ListItem>
                                    <ListItemText 
                                        /* UPDATED KEY HERE */
                                        primary={t('aboutPage.solution.list.trustLedger.primary')} 
                                        secondary={t('aboutPage.solution.list.trustLedger.secondary')} />
                                </ListItem>
                            </List>
                        </Box>
                    </motion.div>
                    
                    {/* <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                        <Box className="ap-section">
                            <Typography variant="h5" component="h2">{t('aboutPage.team.title')}</Typography>
                            <div className="team-member">
                                <Typography variant="h6" component="h3">{t('aboutPage.team.rob.name')}</Typography>
                                <Typography variant="subtitle1" className="team-member-role" gutterBottom>{t('aboutPage.team.rob.role')}</Typography>
                                <Typography paragraph>{t('aboutPage.team.rob.bio')}</Typography>
                            </div>
                            <div className="team-member">
                                <Typography variant="h6" component="h3">{t('aboutPage.team.diego.name')}</Typography>
                                <Typography variant="subtitle1" className="team-member-role" gutterBottom>{t('aboutPage.team.diego.role')}</Typography>
                                <Typography paragraph>{t('aboutPage.team.diego.bio')}</Typography>
                            </div>
                        </Box>
                    </motion.div>

                    <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                        <Box className="ap-section">
                            <Typography variant="h5" component="h2">{t('aboutPage.advisory.title')}</Typography>
                            <div className="team-member">
                                <Typography variant="h6" component="h3">{t('aboutPage.advisory.gary.name')}</Typography>
                                <Typography variant="subtitle1" className="team-member-role" gutterBottom>{t('aboutPage.advisory.gary.role')}</Typography>
                                <Typography paragraph sx={{ whiteSpace: 'pre-wrap' }}>{t('aboutPage.advisory.gary.bio')}</Typography>
                            </div>
                            <div className="team-member">
                                <Typography variant="h6" component="h3">{t('aboutPage.advisory.arun.name')}</Typography>
                                <Typography variant="subtitle1" className="team-member-role" gutterBottom>{t('aboutPage.advisory.arun.role')}</Typography>
                                <Typography paragraph>{t('aboutPage.advisory.arun.bio')}</Typography>
                            </div>
                            <div className="team-member">
                                <Typography variant="h6" component="h3">{t('aboutPage.advisory.ian.name')}</Typography>
                                <Typography variant="subtitle1" className="team-member-role" gutterBottom>{t('aboutPage.advisory.ian.role')}</Typography>
                                <Typography paragraph>{t('aboutPage.advisory.ian.bio')}</Typography>
                            </div>
                        </Box>
                    </motion.div> */}
                </Box>
            </main>
        </div>
    );
};

export default AboutPage;